import React, { useState, useMemo } from 'react';
import { Card, Form, DatePicker, Button, Space, Table, Typography, Modal, Statistic, Row, Col, Select, Input, message, Tabs } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { EyeOutlined, BarChartOutlined, TrophyOutlined, DownloadOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { Option } = Select;

// 使用固定随机种子生成数据
function seededRandom(seed: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 0xffffffff;
}

// 生成随机金额
function randomAmount(seed: string, min: number, max: number): number {
  return parseFloat((min + seededRandom(seed) * (max - min)).toFixed(2));
}

// 生成随机数量
function randomCount(seed: string, min: number, max: number): number {
  return Math.floor(min + seededRandom(seed) * (max - min));
}

// 主播信息
const STREAMERS = [
  { id: 1, name: 'Alice', code: 'S001' },
  { id: 2, name: 'Bob', code: 'S002' },
  { id: 3, name: 'Charlie', code: 'S003' },
  { id: 4, name: 'Diana', code: 'S004' },
  { id: 5, name: 'Eve', code: 'S005' },
];

// 游戏类型
const GAME_TYPES = [
  { id: 1, name: '百家乐', code: 'BACCARAT' },
  { id: 2, name: '鱼虾蟹', code: 'FISH_CRAB_SHRIMP' },
  { id: 3, name: '骰宝', code: 'SICBO' },
  { id: 4, name: '轮盘', code: 'ROULETTE' },
  { id: 5, name: '龙虎', code: 'DRAGON_TIGER' },
];

// 生成聚合统计数据（时间范围内单条数据）
function generatePlatformStats(startDate: string, endDate: string, streamerId?: number, gameType?: number, liveRoomId?: string) {
  const seed = `platform_${startDate}_${endDate}_${streamerId || 'all'}_${gameType || 'all'}_${liveRoomId || 'all'}`;
  return {
    stat_date_range: `${startDate} 至 ${endDate}`,
    total_duration_minutes: randomCount(seed + '_duration', 3000, 8000),
    total_viewers: randomCount(seed + '_viewers', 10000, 30000),
    max_viewers: randomCount(seed + '_max', 5000, 12000),
    bet_users: randomCount(seed + '_bet_users', 2000, 6000),
    bet_count: randomCount(seed + '_bet_count', 10000, 35000),
    bet_amount: randomAmount(seed + '_bet', 400000, 800000),
    bet_valid_amount: randomAmount(seed + '_valid', 380000, 780000),
    payout_amount: randomAmount(seed + '_payout', 380000, 750000),
    win_loss_amount: randomAmount(seed + '_wl', -30000, 10000),
    gift_amount: randomAmount(seed + '_gift', 80000, 200000),
    gift_count: randomCount(seed + '_gift_count', 2000, 6000),
    paid_amount: randomAmount(seed + '_paid', 50000, 150000), // 付费金额(付费直播)
    new_users_today: randomCount(seed + '_new', 500, 1500),
    comment_users: randomCount(seed + '_comment_users', 3000, 8000),
    comment_count: randomCount(seed + '_comment_count', 15000, 50000),
    active_streamers: randomCount(seed + '_active', 3, 5),
    live_session_count: randomCount(seed + '_sessions', 50, 120),
  };
}

// 生成主播统计数据（时间范围内）
function generateStreamerStats(streamerId: number, startDate: string, endDate: string) {
  const seed = `streamer_${streamerId}_${startDate}_${endDate}`;
  return {
    streamer_id: streamerId,
    streamer_name: STREAMERS.find(s => s.id === streamerId)?.name || 'Unknown',
    total_duration_minutes: randomCount(seed + '_duration', 600, 2400),
    total_viewers: randomCount(seed + '_viewers', 2000, 8000),
    max_viewers: randomCount(seed + '_max', 1500, 5000),
    bet_users: randomCount(seed + '_bet_users', 500, 2000),
    bet_count: randomCount(seed + '_bet_count', 2000, 10000),
    bet_amount: randomAmount(seed + '_bet', 100000, 250000),
    bet_valid_amount: randomAmount(seed + '_valid', 95000, 245000),
    payout_amount: randomAmount(seed + '_payout', 95000, 240000),
    win_loss_amount: randomAmount(seed + '_wl', -10000, 5000),
    gift_amount: randomAmount(seed + '_gift', 20000, 80000),
    gift_count: randomCount(seed + '_gift_count', 500, 2000),
    paid_amount: randomAmount(seed + '_paid', 10000, 40000), // 付费金额(付费直播)
    new_users_today: randomCount(seed + '_new', 100, 500),
    comment_users: randomCount(seed + '_comment_users', 800, 3000),
    comment_count: randomCount(seed + '_comment_count', 4000, 15000),
    live_session_count: randomCount(seed + '_sessions', 10, 40),
  };
}

// 生成游戏统计数据（时间范围内）
function generateGameStats(gameTypeId: number, startDate: string, endDate: string) {
  const seed = `game_${gameTypeId}_${startDate}_${endDate}`;
  return {
    game_type_id: gameTypeId,
    game_type_name: GAME_TYPES.find(g => g.id === gameTypeId)?.name || 'Unknown',
    total_duration_minutes: randomCount(seed + '_duration', 800, 3000),
    total_viewers: randomCount(seed + '_viewers', 3000, 12000),
    max_viewers: randomCount(seed + '_max', 2000, 6000),
    bet_users: randomCount(seed + '_bet_users', 800, 3000),
    bet_count: randomCount(seed + '_bet_count', 5000, 20000),
    bet_amount: randomAmount(seed + '_bet', 200000, 500000),
    bet_valid_amount: randomAmount(seed + '_valid', 190000, 490000),
    payout_amount: randomAmount(seed + '_payout', 190000, 480000),
    win_loss_amount: randomAmount(seed + '_wl', -20000, 10000),
    gift_amount: randomAmount(seed + '_gift', 40000, 120000),
    gift_count: randomCount(seed + '_gift_count', 1000, 4000),
    new_users_today: randomCount(seed + '_new', 200, 800),
    comment_users: randomCount(seed + '_comment_users', 1500, 5000),
    comment_count: randomCount(seed + '_comment_count', 8000, 30000),
    live_session_count: randomCount(seed + '_sessions', 15, 60),
  };
}

// 生成明细数据（时间范围内）
function generateDetails(streamerId: number, startDate: string, endDate: string, sessionIndex: number) {
  const seed = `detail_${streamerId}_${startDate}_${endDate}_${sessionIndex}`;
  const baseDate = dayjs(startDate);
  const sessionDate = baseDate.add(sessionIndex % 7, 'day');
  const startHour = 10 + (sessionIndex % 5) * 3;
  const duration = 60 + randomCount(seed + '_dur', 0, 120);
  
  return {
    id: parseInt(`${streamerId}${sessionIndex}${sessionDate.format('YYYYMMDD')}`),
    streamer_id: streamerId,
    streamer_name: STREAMERS.find(s => s.id === streamerId)?.name || 'Unknown',
    live_date: sessionDate.format('YYYY-MM-DD'),
    start_time: `${sessionDate.format('YYYY-MM-DD')} ${startHour.toString().padStart(2, '0')}:00:00`,
    end_time: `${sessionDate.format('YYYY-MM-DD')} ${(startHour + Math.floor(duration / 60)).toString().padStart(2, '0')}:${(duration % 60).toString().padStart(2, '0')}:00`,
    duration_minutes: duration,
    total_viewers: randomCount(seed + '_viewers', 200, 800),
    max_viewers: randomCount(seed + '_max', 150, 500),
    bet_users: randomCount(seed + '_bet_users', 30, 150),
    bet_count: randomCount(seed + '_bet_count', 100, 600),
    bet_amount: randomAmount(seed + '_bet', 5000, 15000),
    bet_valid_amount: randomAmount(seed + '_valid', 4800, 14500),
    payout_amount: randomAmount(seed + '_payout', 4800, 14000),
    win_loss_amount: randomAmount(seed + '_wl', -500, 300),
    gift_amount: randomAmount(seed + '_gift', 1000, 4000),
    gift_count: randomCount(seed + '_gift_count', 25, 100),
    paid_amount: randomAmount(seed + '_paid', 500, 3000), // 付费金额(付费直播)
    new_users_today: randomCount(seed + '_new', 5, 25),
    comment_users: randomCount(seed + '_comment_users', 40, 150),
    comment_count: randomCount(seed + '_comment_count', 200, 800),
    status: 1,
  };
}

const PEOPLE_FIELD_LABELS = {
  total_viewers: '累计观看人数',
  max_viewers: '最大观看人数',
  bet_users: '投注人数',
  new_users_today: '当日新用户',
  comment_users: '评论人数',
} as const;

type PeopleMetricKey = keyof typeof PEOPLE_FIELD_LABELS;

type UserListContext = {
  contextLabel: string;
  dateLabel?: string;
  seedKey: string;
  startTime?: string;
  endTime?: string;
};

function sanitizeFileName(name: string) {
  if (!name) return 'export';
  return name.replace(/[\\/:*?"<>|]/g, '_');
}

function exportToCSV(
  filename: string,
  columns: { title: string; dataIndex: string }[],
  data: any[],
) {
  if (!data || data.length === 0) {
    message.warning('暂无可导出的数据');
    return;
  }

  const header = columns.map(col => col.title).join(',');
  const escapeCell = (value: any) => {
    if (value === null || value === undefined) return '';
    const str = String(value).replace(/"/g, '""');
    if (str.search(/("|,|\n)/g) >= 0) {
      return `"${str}"`;
    }
    return str;
  };
  const rows = data.map(row =>
    columns
      .map(col => escapeCell(row[col.dataIndex]))
      .join(','),
  );
  const csvContent = ['\uFEFF' + header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function pickExportColumns(columns: any[]) {
  return columns
    .filter(col => col.dataIndex && typeof col.title === 'string')
    .map(col => ({ title: col.title as string, dataIndex: col.dataIndex as string }));
}

function generateUserListRecords(
  seedKey: string,
  startTime?: string,
  endTime?: string,
  size = 50,
) {
  const start = startTime ? dayjs(startTime) : dayjs().subtract(1, 'day');
  const end = endTime ? dayjs(endTime) : start.add(6, 'hour');
  const effectiveEnd = end.isAfter(start) ? end : start.add(2, 'hour');
  const durationMinutes = Math.max(effectiveEnd.diff(start, 'minute'), 120);

  const records = [];
  for (let i = 0; i < size; i++) {
    const rowSeed = `${seedKey}_${i}`;
    const enterOffset = Math.floor(seededRandom(rowSeed + '_enter') * durationMinutes);
    const stayMinutes = Math.max(5, Math.floor(seededRandom(rowSeed + '_stay') * 180));
    const leaveOffset = Math.min(durationMinutes, enterOffset + stayMinutes);
    const enterTime = start.add(enterOffset, 'minute');
    const leaveTime = start.add(leaveOffset, 'minute');
    records.push({
      user_id: `U${Math.floor(100000 + seededRandom(rowSeed + '_id') * 900000)}`,
      first_enter_time: enterTime.format('YYYY-MM-DD HH:mm:ss'),
      last_leave_time: leaveTime.format('YYYY-MM-DD HH:mm:ss'),
      duration_minutes: Math.max(1, leaveTime.diff(enterTime, 'minute')),
    });
  }
  return records;
}

function generateBetUserRecords(seedKey: string, size = 50) {
  const records = [];
  for (let i = 0; i < size; i++) {
    const rowSeed = `${seedKey}_${i}`;
    const betAmount = randomAmount(rowSeed + '_bet', 2000, 50000);
    const validRate = 0.5 + seededRandom(rowSeed + '_rate') * 0.5;
    const validBetAmount = parseFloat((betAmount * validRate).toFixed(2));
    const payoutAmount = randomAmount(rowSeed + '_payout', betAmount * 0.2, betAmount * 1.2);
    const winLossAmount = parseFloat((payoutAmount - betAmount).toFixed(2));
    records.push({
      user_id: `U${Math.floor(100000 + seededRandom(rowSeed + '_id') * 900000)}`,
      bet_amount: parseFloat(betAmount.toFixed(2)),
      bet_valid_amount: validBetAmount,
      payout_amount: parseFloat(payoutAmount.toFixed(2)),
      win_loss_amount: winLossAmount,
    });
  }
  return records;
}

const LiveStreamReport: React.FC = () => {
  const [form] = Form.useForm();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs().subtract(1, 'day'),
  ]);
  const [streamerId, setStreamerId] = useState<number | undefined>(undefined);
  const [gameType, setGameType] = useState<number | undefined>(undefined);
  const [liveRoomId, setLiveRoomId] = useState<string | undefined>(undefined);
  
  // 弹窗状态
  const [streamerModalVisible, setStreamerModalVisible] = useState(false);
  const [gameModalVisible, setGameModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [userListModalVisible, setUserListModalVisible] = useState(false);
  const [userListTitle, setUserListTitle] = useState('');
  const [userListExportName, setUserListExportName] = useState('用户列表');
  const [userListData, setUserListData] = useState<any[]>([]);
  const [userListMode, setUserListMode] = useState<'viewer' | 'bet'>('viewer');

  const rangeTimeStrings = useMemo(() => {
    if (!range || !range[0] || !range[1]) {
      return { start: undefined, end: undefined };
    }
    return {
      start: range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };
  }, [range]);

  const selectedRangeTimeStrings = useMemo(() => {
    if (!selectedDateRange.start || !selectedDateRange.end) {
      return { start: undefined, end: undefined };
    }
    return {
      start: `${selectedDateRange.start} 00:00:00`,
      end: `${selectedDateRange.end} 23:59:59`,
    };
  }, [selectedDateRange]);

  const handlePeopleMetricClick = (fieldKey: PeopleMetricKey, value: number, context: UserListContext) => {
    const metricLabel = PEOPLE_FIELD_LABELS[fieldKey] || '人数';
    const dateSegment = context.dateLabel ? `（${context.dateLabel}）` : '';
    const titleText = `${context.contextLabel}｜${metricLabel}${dateSegment}`;
    const estimatedCount = Math.round(value / 200);
    const rowCount = Math.min(300, Math.max(20, estimatedCount || 0));
    let records: any[] = [];
    let mode: 'viewer' | 'bet' = 'viewer';

    if (fieldKey === 'bet_users') {
      records = generateBetUserRecords(`${context.seedKey}_${fieldKey}`, rowCount);
      mode = 'bet';
    } else {
      records = generateUserListRecords(
        `${context.seedKey}_${fieldKey}`,
        context.startTime,
        context.endTime,
        rowCount,
      );
    }

    setUserListTitle(titleText);
    setUserListData(records);
    setUserListExportName(sanitizeFileName(`${context.contextLabel}_${metricLabel}`));
    setUserListMode(mode);
    setUserListModalVisible(true);
  };

  const renderPeopleCount = (
    fieldKey: PeopleMetricKey,
    contextBuilder: (record: any, value: number) => UserListContext,
  ) => (value: number, record: any) => {
    if (typeof value !== 'number') {
      return value;
    }
    const handleClick = () => handlePeopleMetricClick(fieldKey, value, contextBuilder(record, value));
    return (
      <Button type="link" size="small" style={{ padding: 0 }} onClick={handleClick}>
        {value.toLocaleString()}
      </Button>
    );
  };

  const handleDownload = (baseName: string, columns: any[], data: any[]) => {
    const exportCols = pickExportColumns(columns);
    exportToCSV(`${sanitizeFileName(baseName)}.csv`, exportCols, data);
  };

  // 生成总表数据（单条聚合数据）
  const platformData = useMemo(() => {
    if (!range || !range[0] || !range[1]) return [];
    const startDate = range[0].format('YYYY-MM-DD');
    const endDate = range[1].format('YYYY-MM-DD');
    const data = generatePlatformStats(startDate, endDate, streamerId, gameType, liveRoomId);
    return [data];
  }, [range, streamerId, gameType, liveRoomId]);

  // 生成主播统计数据（时间范围内）- 按日期分组
  const streamerDataByDate = useMemo(() => {
    if (!selectedDateRange.start || !selectedDateRange.end) return {};
    const dataByDate: Record<string, any[]> = {};
    const start = dayjs(selectedDateRange.start);
    const end = dayjs(selectedDateRange.end);
    let current = start;
    
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const dateStr = current.format('YYYY-MM-DD');
      dataByDate[dateStr] = [];
      // 如果指定了主播ID，只显示该主播；否则显示所有主播
      const streamersToShow = streamerId 
        ? STREAMERS.filter(s => s.id === streamerId)
        : STREAMERS;
      for (const streamer of streamersToShow) {
        dataByDate[dateStr].push(generateStreamerStats(streamer.id, dateStr, dateStr));
      }
      current = current.add(1, 'day');
    }
    return dataByDate;
  }, [selectedDateRange, streamerId]);

  // 生成主播数据（时间范围内）- 汇总数据
  const streamerData = useMemo(() => {
    if (!selectedDateRange.start || !selectedDateRange.end) return [];
    const data = [];
    // 如果指定了主播ID，只显示该主播；否则显示所有主播
    const streamersToShow = streamerId 
      ? STREAMERS.filter(s => s.id === streamerId)
      : STREAMERS;
    for (const streamer of streamersToShow) {
      data.push(generateStreamerStats(streamer.id, selectedDateRange.start, selectedDateRange.end));
    }
    return data;
  }, [selectedDateRange, streamerId]);

  // 生成游戏统计数据（时间范围内）
  const gameData = useMemo(() => {
    if (!selectedDateRange.start || !selectedDateRange.end) return [];
    const data = [];
    // 如果指定了游戏类型，只显示该游戏；否则显示所有游戏
    const gamesToShow = gameType 
      ? GAME_TYPES.filter(g => g.id === gameType)
      : GAME_TYPES;
    for (const game of gamesToShow) {
      data.push(generateGameStats(game.id, selectedDateRange.start, selectedDateRange.end));
    }
    return data;
  }, [selectedDateRange, gameType]);

  // 生成明细数据（时间范围内）
  const detailData = useMemo(() => {
    if (!selectedDateRange.start || !selectedDateRange.end) return [];
    const data = [];
    const streamersToShow = streamerId 
      ? STREAMERS.filter(s => s.id === streamerId)
      : STREAMERS;
    for (const streamer of streamersToShow) {
      const sessionCount = Math.floor(10 + seededRandom(`sess_${streamer.id}_${selectedDateRange.start}_${selectedDateRange.end}`) * 20);
      for (let i = 0; i < sessionCount; i++) {
        data.push(generateDetails(streamer.id, selectedDateRange.start, selectedDateRange.end, i));
      }
    }
    return data.sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [selectedDateRange, streamerId]);

  // 总表列定义
  const platformColumns = [
    {
      title: '统计日期',
      dataIndex: 'stat_date_range',
      key: 'stat_date_range',
      fixed: 'left' as const,
      width: 200,
      align: 'center' as const,
    },
    {
      title: '直播总时长（分钟）',
      dataIndex: 'total_duration_minutes',
      key: 'total_duration_minutes',
      width: 150,
      sorter: (a: any, b: any) => a.total_duration_minutes - b.total_duration_minutes,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '累计观看人数',
      dataIndex: 'total_viewers',
      key: 'total_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.total_viewers - b.total_viewers,
      align: 'right' as const,
      render: renderPeopleCount('total_viewers', (record) => ({
        contextLabel: '全平台',
        dateLabel: record.stat_date_range,
        seedKey: `platform_${record.stat_date_range}`,
        startTime: rangeTimeStrings.start,
        endTime: rangeTimeStrings.end,
      })),
    },
    {
      title: '最大观看人数',
      dataIndex: 'max_viewers',
      key: 'max_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.max_viewers - b.max_viewers,
      align: 'right' as const,
      render: renderPeopleCount('max_viewers', (record) => ({
        contextLabel: '全平台',
        dateLabel: record.stat_date_range,
        seedKey: `platform_${record.stat_date_range}_max`,
        startTime: rangeTimeStrings.start,
        endTime: rangeTimeStrings.end,
      })),
    },
    {
      title: '投注人数',
      dataIndex: 'bet_users',
      key: 'bet_users',
      width: 100,
      sorter: (a: any, b: any) => a.bet_users - b.bet_users,
      align: 'right' as const,
      render: renderPeopleCount('bet_users', (record) => ({
        contextLabel: '全平台',
        dateLabel: record.stat_date_range,
        seedKey: `platform_${record.stat_date_range}_bet`,
        startTime: rangeTimeStrings.start,
        endTime: rangeTimeStrings.end,
      })),
    },
    {
      title: '投注笔数',
      dataIndex: 'bet_count',
      key: 'bet_count',
      width: 100,
      sorter: (a: any, b: any) => a.bet_count - b.bet_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '总投注金额',
      dataIndex: 'bet_amount',
      key: 'bet_amount',
      width: 130,
      sorter: (a: any, b: any) => a.bet_amount - b.bet_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注有效金额',
      dataIndex: 'bet_valid_amount',
      key: 'bet_valid_amount',
      width: 140,
      sorter: (a: any, b: any) => a.bet_valid_amount - b.bet_valid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '派彩金额',
      dataIndex: 'payout_amount',
      key: 'payout_amount',
      width: 120,
      sorter: (a: any, b: any) => a.payout_amount - b.payout_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注输赢',
      dataIndex: 'win_loss_amount',
      key: 'win_loss_amount',
      width: 120,
      sorter: (a: any, b: any) => a.win_loss_amount - b.win_loss_amount,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ color: val >= 0 ? '#cf1322' : '#3f8600' }}>
          {val >= 0 ? '+' : ''}¥{val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: '打赏金额',
      dataIndex: 'gift_amount',
      key: 'gift_amount',
      width: 120,
      sorter: (a: any, b: any) => a.gift_amount - b.gift_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '打赏数量',
      dataIndex: 'gift_count',
      key: 'gift_count',
      width: 100,
      sorter: (a: any, b: any) => a.gift_count - b.gift_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '付费金额(付费直播)',
      dataIndex: 'paid_amount',
      key: 'paid_amount',
      width: 150,
      sorter: (a: any, b: any) => a.paid_amount - b.paid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '当日新用户',
      dataIndex: 'new_users_today',
      key: 'new_users_today',
      width: 120,
      sorter: (a: any, b: any) => a.new_users_today - b.new_users_today,
      align: 'right' as const,
      render: renderPeopleCount('new_users_today', (record) => ({
        contextLabel: '全平台',
        dateLabel: record.stat_date_range,
        seedKey: `platform_${record.stat_date_range}_new`,
        startTime: rangeTimeStrings.start,
        endTime: rangeTimeStrings.end,
      })),
    },
    {
      title: '评论人数',
      dataIndex: 'comment_users',
      key: 'comment_users',
      width: 100,
      sorter: (a: any, b: any) => a.comment_users - b.comment_users,
      align: 'right' as const,
      render: renderPeopleCount('comment_users', (record) => ({
        contextLabel: '全平台',
        dateLabel: record.stat_date_range,
        seedKey: `platform_${record.stat_date_range}_comment`,
        startTime: rangeTimeStrings.start,
        endTime: rangeTimeStrings.end,
      })),
    },
    {
      title: '评论次数',
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 100,
      sorter: (a: any, b: any) => a.comment_count - b.comment_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '活跃主播数',
      dataIndex: 'active_streamers',
      key: 'active_streamers',
      width: 100,
      align: 'right' as const,
      sorter: (a: any, b: any) => a.active_streamers - b.active_streamers,
    },
    {
      title: '直播场次',
      dataIndex: 'live_session_count',
      key: 'live_session_count',
      width: 100,
      align: 'right' as const,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 220,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => handleViewStreamerStats()}
          >
            主播统计
          </Button>
          <Button
            type="link"
            icon={<TrophyOutlined />}
            onClick={() => handleViewGameStats()}
          >
            游戏统计
          </Button>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails()}
          >
            明细
          </Button>
        </Space>
      ),
    },
  ];

  // 游戏统计列定义
  const gameColumns = [
    {
      title: '游戏名称',
      dataIndex: 'game_type_name',
      key: 'game_type_name',
      fixed: 'left' as const,
      width: 100,
    },
    {
      title: '直播总时长（分钟）',
      dataIndex: 'total_duration_minutes',
      key: 'total_duration_minutes',
      width: 150,
      sorter: (a: any, b: any) => a.total_duration_minutes - b.total_duration_minutes,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '累计观看人数',
      dataIndex: 'total_viewers',
      key: 'total_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.total_viewers - b.total_viewers,
      align: 'right' as const,
      render: renderPeopleCount('total_viewers', (record) => ({
        contextLabel: `游戏-${record.game_type_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `game_${record.game_type_id}`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '最大观看人数',
      dataIndex: 'max_viewers',
      key: 'max_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.max_viewers - b.max_viewers,
      align: 'right' as const,
      render: renderPeopleCount('max_viewers', (record) => ({
        contextLabel: `游戏-${record.game_type_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `game_${record.game_type_id}_max`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '投注人数',
      dataIndex: 'bet_users',
      key: 'bet_users',
      width: 100,
      sorter: (a: any, b: any) => a.bet_users - b.bet_users,
      align: 'right' as const,
      render: renderPeopleCount('bet_users', (record) => ({
        contextLabel: `游戏-${record.game_type_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `game_${record.game_type_id}_bet`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '投注笔数',
      dataIndex: 'bet_count',
      key: 'bet_count',
      width: 100,
      sorter: (a: any, b: any) => a.bet_count - b.bet_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '总投注金额',
      dataIndex: 'bet_amount',
      key: 'bet_amount',
      width: 130,
      sorter: (a: any, b: any) => a.bet_amount - b.bet_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注有效金额',
      dataIndex: 'bet_valid_amount',
      key: 'bet_valid_amount',
      width: 140,
      sorter: (a: any, b: any) => a.bet_valid_amount - b.bet_valid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '派彩金额',
      dataIndex: 'payout_amount',
      key: 'payout_amount',
      width: 120,
      sorter: (a: any, b: any) => a.payout_amount - b.payout_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注输赢',
      dataIndex: 'win_loss_amount',
      key: 'win_loss_amount',
      width: 120,
      sorter: (a: any, b: any) => a.win_loss_amount - b.win_loss_amount,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ color: val >= 0 ? '#cf1322' : '#3f8600' }}>
          {val >= 0 ? '+' : ''}¥{val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: '打赏金额',
      dataIndex: 'gift_amount',
      key: 'gift_amount',
      width: 120,
      sorter: (a: any, b: any) => a.gift_amount - b.gift_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '打赏数量',
      dataIndex: 'gift_count',
      key: 'gift_count',
      width: 100,
      sorter: (a: any, b: any) => a.gift_count - b.gift_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '当日新用户',
      dataIndex: 'new_users_today',
      key: 'new_users_today',
      width: 120,
      sorter: (a: any, b: any) => a.new_users_today - b.new_users_today,
      align: 'right' as const,
      render: renderPeopleCount('new_users_today', (record) => ({
        contextLabel: `游戏-${record.game_type_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `game_${record.game_type_id}_new`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '评论人数',
      dataIndex: 'comment_users',
      key: 'comment_users',
      width: 100,
      sorter: (a: any, b: any) => a.comment_users - b.comment_users,
      align: 'right' as const,
      render: renderPeopleCount('comment_users', (record) => ({
        contextLabel: `游戏-${record.game_type_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `game_${record.game_type_id}_comment`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '评论次数',
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 100,
      sorter: (a: any, b: any) => a.comment_count - b.comment_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '直播场次',
      dataIndex: 'live_session_count',
      key: 'live_session_count',
      width: 100,
      align: 'right' as const,
      sorter: (a: any, b: any) => a.live_session_count - b.live_session_count,
    },
  ];

  // 主播统计列定义
  const streamerColumns = [
    {
      title: '主播名称',
      dataIndex: 'streamer_name',
      key: 'streamer_name',
      fixed: 'left' as const,
      width: 100,
    },
    {
      title: '直播总时长（分钟）',
      dataIndex: 'total_duration_minutes',
      key: 'total_duration_minutes',
      width: 150,
      sorter: (a: any, b: any) => a.total_duration_minutes - b.total_duration_minutes,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '累计观看人数',
      dataIndex: 'total_viewers',
      key: 'total_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.total_viewers - b.total_viewers,
      align: 'right' as const,
      render: renderPeopleCount('total_viewers', (record) => ({
        contextLabel: `主播-${record.streamer_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `streamer_${record.streamer_id}`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '最大观看人数',
      dataIndex: 'max_viewers',
      key: 'max_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.max_viewers - b.max_viewers,
      align: 'right' as const,
      render: renderPeopleCount('max_viewers', (record) => ({
        contextLabel: `主播-${record.streamer_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `streamer_${record.streamer_id}_max`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '投注人数',
      dataIndex: 'bet_users',
      key: 'bet_users',
      width: 100,
      sorter: (a: any, b: any) => a.bet_users - b.bet_users,
      align: 'right' as const,
      render: renderPeopleCount('bet_users', (record) => ({
        contextLabel: `主播-${record.streamer_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `streamer_${record.streamer_id}_bet`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '投注笔数',
      dataIndex: 'bet_count',
      key: 'bet_count',
      width: 100,
      sorter: (a: any, b: any) => a.bet_count - b.bet_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '总投注金额',
      dataIndex: 'bet_amount',
      key: 'bet_amount',
      width: 130,
      sorter: (a: any, b: any) => a.bet_amount - b.bet_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注有效金额',
      dataIndex: 'bet_valid_amount',
      key: 'bet_valid_amount',
      width: 140,
      sorter: (a: any, b: any) => a.bet_valid_amount - b.bet_valid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '派彩金额',
      dataIndex: 'payout_amount',
      key: 'payout_amount',
      width: 120,
      sorter: (a: any, b: any) => a.payout_amount - b.payout_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注输赢',
      dataIndex: 'win_loss_amount',
      key: 'win_loss_amount',
      width: 120,
      sorter: (a: any, b: any) => a.win_loss_amount - b.win_loss_amount,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ color: val >= 0 ? '#cf1322' : '#3f8600' }}>
          {val >= 0 ? '+' : ''}¥{val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: '打赏金额',
      dataIndex: 'gift_amount',
      key: 'gift_amount',
      width: 120,
      sorter: (a: any, b: any) => a.gift_amount - b.gift_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '打赏数量',
      dataIndex: 'gift_count',
      key: 'gift_count',
      width: 100,
      sorter: (a: any, b: any) => a.gift_count - b.gift_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '付费金额(付费直播)',
      dataIndex: 'paid_amount',
      key: 'paid_amount',
      width: 150,
      sorter: (a: any, b: any) => a.paid_amount - b.paid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '当日新用户',
      dataIndex: 'new_users_today',
      key: 'new_users_today',
      width: 120,
      sorter: (a: any, b: any) => a.new_users_today - b.new_users_today,
      align: 'right' as const,
      render: renderPeopleCount('new_users_today', (record) => ({
        contextLabel: `主播-${record.streamer_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `streamer_${record.streamer_id}_new`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '评论人数',
      dataIndex: 'comment_users',
      key: 'comment_users',
      width: 100,
      sorter: (a: any, b: any) => a.comment_users - b.comment_users,
      align: 'right' as const,
      render: renderPeopleCount('comment_users', (record) => ({
        contextLabel: `主播-${record.streamer_name}`,
        dateLabel: `${selectedDateRange.start} 至 ${selectedDateRange.end}`,
        seedKey: `streamer_${record.streamer_id}_comment`,
        startTime: selectedRangeTimeStrings.start,
        endTime: selectedRangeTimeStrings.end,
      })),
    },
    {
      title: '评论次数',
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 100,
      sorter: (a: any, b: any) => a.comment_count - b.comment_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '直播场次',
      dataIndex: 'live_session_count',
      key: 'live_session_count',
      width: 100,
      align: 'right' as const,
      sorter: (a: any, b: any) => a.live_session_count - b.live_session_count,
    },
  ];

  // 明细列定义
  const detailColumns = [
    {
      title: '主播名称',
      dataIndex: 'streamer_name',
      key: 'streamer_name',
      fixed: 'left' as const,
      width: 100,
    },
    {
      title: '开播时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
    },
    {
      title: '关播时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 160,
    },
    {
      title: '直播时长（分钟）',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      width: 150,
      sorter: (a: any, b: any) => a.duration_minutes - b.duration_minutes,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '累计观看人数',
      dataIndex: 'total_viewers',
      key: 'total_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.total_viewers - b.total_viewers,
      align: 'right' as const,
      render: renderPeopleCount('total_viewers', (record) => ({
        contextLabel: `明细-${record.streamer_name}`,
        dateLabel: record.live_date,
        seedKey: `detail_${record.id}`,
        startTime: record.start_time,
        endTime: record.end_time,
      })),
    },
    {
      title: '最大观看人数',
      dataIndex: 'max_viewers',
      key: 'max_viewers',
      width: 130,
      sorter: (a: any, b: any) => a.max_viewers - b.max_viewers,
      align: 'right' as const,
      render: renderPeopleCount('max_viewers', (record) => ({
        contextLabel: `明细-${record.streamer_name}`,
        dateLabel: record.live_date,
        seedKey: `detail_${record.id}_max`,
        startTime: record.start_time,
        endTime: record.end_time,
      })),
    },
    {
      title: '投注人数',
      dataIndex: 'bet_users',
      key: 'bet_users',
      width: 100,
      sorter: (a: any, b: any) => a.bet_users - b.bet_users,
      align: 'right' as const,
      render: renderPeopleCount('bet_users', (record) => ({
        contextLabel: `明细-${record.streamer_name}`,
        dateLabel: record.live_date,
        seedKey: `detail_${record.id}_bet`,
        startTime: record.start_time,
        endTime: record.end_time,
      })),
    },
    {
      title: '投注笔数',
      dataIndex: 'bet_count',
      key: 'bet_count',
      width: 100,
      sorter: (a: any, b: any) => a.bet_count - b.bet_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '总投注金额',
      dataIndex: 'bet_amount',
      key: 'bet_amount',
      width: 130,
      sorter: (a: any, b: any) => a.bet_amount - b.bet_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注有效金额',
      dataIndex: 'bet_valid_amount',
      key: 'bet_valid_amount',
      width: 140,
      sorter: (a: any, b: any) => a.bet_valid_amount - b.bet_valid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '派彩金额',
      dataIndex: 'payout_amount',
      key: 'payout_amount',
      width: 120,
      sorter: (a: any, b: any) => a.payout_amount - b.payout_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '投注输赢',
      dataIndex: 'win_loss_amount',
      key: 'win_loss_amount',
      width: 120,
      sorter: (a: any, b: any) => a.win_loss_amount - b.win_loss_amount,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ color: val >= 0 ? '#cf1322' : '#3f8600' }}>
          {val >= 0 ? '+' : ''}¥{val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: '打赏金额',
      dataIndex: 'gift_amount',
      key: 'gift_amount',
      width: 120,
      sorter: (a: any, b: any) => a.gift_amount - b.gift_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '打赏数量',
      dataIndex: 'gift_count',
      key: 'gift_count',
      width: 100,
      sorter: (a: any, b: any) => a.gift_count - b.gift_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: '付费金额(付费直播)',
      dataIndex: 'paid_amount',
      key: 'paid_amount',
      width: 150,
      sorter: (a: any, b: any) => a.paid_amount - b.paid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${val.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '当日新用户',
      dataIndex: 'new_users_today',
      key: 'new_users_today',
      width: 120,
      sorter: (a: any, b: any) => a.new_users_today - b.new_users_today,
      align: 'right' as const,
      render: renderPeopleCount('new_users_today', (record) => ({
        contextLabel: `明细-${record.streamer_name}`,
        dateLabel: record.live_date,
        seedKey: `detail_${record.id}_new`,
        startTime: record.start_time,
        endTime: record.end_time,
      })),
    },
    {
      title: '评论人数',
      dataIndex: 'comment_users',
      key: 'comment_users',
      width: 100,
      sorter: (a: any, b: any) => a.comment_users - b.comment_users,
      align: 'right' as const,
      render: renderPeopleCount('comment_users', (record) => ({
        contextLabel: `明细-${record.streamer_name}`,
        dateLabel: record.live_date,
        seedKey: `detail_${record.id}_comment`,
        startTime: record.start_time,
        endTime: record.end_time,
      })),
    },
    {
      title: '评论次数',
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 100,
      sorter: (a: any, b: any) => a.comment_count - b.comment_count,
      align: 'right' as const,
      render: (val: number) => val.toLocaleString(),
    },
  ];

  const viewerUserColumns = [
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 140,
    },
    {
      title: '最早进入时间',
      dataIndex: 'first_enter_time',
      key: 'first_enter_time',
      width: 200,
    },
    {
      title: '最后离开时间',
      dataIndex: 'last_leave_time',
      key: 'last_leave_time',
      width: 200,
    },
    {
      title: '累计停留时长（分钟）',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      width: 180,
      sorter: (a: any, b: any) => a.duration_minutes - b.duration_minutes,
      align: 'right' as const,
      render: (val: number) => `${val.toLocaleString()} 分钟`,
    },
  ];

  const betUserColumns = [
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 140,
    },
    {
      title: '投注金额',
      dataIndex: 'bet_amount',
      key: 'bet_amount',
      width: 160,
      sorter: (a: any, b: any) => a.bet_amount - b.bet_amount,
      align: 'right' as const,
      render: (val: number) => `¥${Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '有效投注金额',
      dataIndex: 'bet_valid_amount',
      key: 'bet_valid_amount',
      width: 180,
      sorter: (a: any, b: any) => a.bet_valid_amount - b.bet_valid_amount,
      align: 'right' as const,
      render: (val: number) => `¥${Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '派彩金额',
      dataIndex: 'payout_amount',
      key: 'payout_amount',
      width: 160,
      sorter: (a: any, b: any) => a.payout_amount - b.payout_amount,
      align: 'right' as const,
      render: (val: number) => `¥${Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: '输赢金额',
      dataIndex: 'win_loss_amount',
      key: 'win_loss_amount',
      width: 160,
      sorter: (a: any, b: any) => a.win_loss_amount - b.win_loss_amount,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ color: Number(val) >= 0 ? '#cf1322' : '#3f8600' }}>
          {Number(val) >= 0 ? '+' : ''}¥{Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const currentUserListColumns = userListMode === 'bet' ? betUserColumns : viewerUserColumns;

  // 查看主播统计
  const handleViewStreamerStats = () => {
    if (!range || !range[0] || !range[1]) return;
    setSelectedDateRange({
      start: range[0].format('YYYY-MM-DD'),
      end: range[1].format('YYYY-MM-DD'),
    });
    setStreamerModalVisible(true);
  };

  // 查看游戏统计
  const handleViewGameStats = () => {
    if (!range || !range[0] || !range[1]) return;
    setSelectedDateRange({
      start: range[0].format('YYYY-MM-DD'),
      end: range[1].format('YYYY-MM-DD'),
    });
    setGameModalVisible(true);
  };

  // 查看明细
  const handleViewDetails = () => {
    if (!range || !range[0] || !range[1]) return;
    setSelectedDateRange({
      start: range[0].format('YYYY-MM-DD'),
      end: range[1].format('YYYY-MM-DD'),
    });
    setDetailModalVisible(true);
  };

  // 计算总计（直接从单条数据获取）
  const totals = useMemo(() => {
    if (platformData.length === 0) return null;
    const item = platformData[0];
    return {
      total_duration_minutes: item.total_duration_minutes,
      total_viewers: item.total_viewers,
      max_viewers: item.max_viewers,
      bet_users: item.bet_users,
      bet_count: item.bet_count,
      bet_amount: item.bet_amount,
      bet_valid_amount: item.bet_valid_amount,
      payout_amount: item.payout_amount,
      win_loss_amount: item.win_loss_amount,
      gift_amount: item.gift_amount,
      gift_count: item.gift_count,
      paid_amount: item.paid_amount,
      new_users_today: item.new_users_today,
      comment_users: item.comment_users,
      comment_count: item.comment_count,
      active_streamers: item.active_streamers,
      live_session_count: item.live_session_count,
    };
  }, [platformData]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>直播报表</Title>
      
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          initialValues={{
            range: range,
            streamerId: undefined,
            gameType: undefined,
            liveRoomId: undefined,
          }}
          onValuesChange={(changed) => {
            if (changed.range) {
              const r = changed.range as [Dayjs, Dayjs];
              if (r && r[0] && r[1]) {
                setRange([r[0], r[1]]);
              }
            }
            if (changed.streamerId !== undefined) {
              setStreamerId(changed.streamerId);
            }
            if (changed.gameType !== undefined) {
              setGameType(changed.gameType);
            }
            if (changed.liveRoomId !== undefined) {
              setLiveRoomId(changed.liveRoomId);
            }
          }}
        >
          <Form.Item label="日期范围" name="range" required>
            <RangePicker allowClear={false} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item label="主播ID" name="streamerId">
            <Select
              allowClear
              placeholder="请选择主播"
              style={{ width: 150 }}
            >
              {STREAMERS.map(streamer => (
                <Option key={streamer.id} value={streamer.id}>
                  {streamer.name} ({streamer.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="游戏类型" name="gameType">
            <Select
              allowClear
              placeholder="请选择游戏类型"
              style={{ width: 150 }}
            >
              {GAME_TYPES.map(game => (
                <Option key={game.id} value={game.id}>
                  {game.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="直播间ID" name="liveRoomId">
            <Input
              placeholder="请输入直播间ID"
              allowClear
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={() => { /* 数据为计算属性 */ }}>
              查询
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 总计卡片 */}
      {totals && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8} md={6}>
              <Statistic
                title="总直播时长（分钟）"
                value={totals.total_duration_minutes}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Statistic
                title="累计观看人数"
                value={totals.total_viewers}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Statistic
                title="总投注金额"
                value={totals.bet_amount}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Statistic
                title="总打赏金额"
                value={totals.gift_amount}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        <Table
          rowKey="stat_date_range"
          dataSource={platformData}
          columns={platformColumns}
          pagination={false}
          sticky
          scroll={{ x: 1800 }}
        />
      </Card>

      {/* 主播统计弹窗 - 分表显示 */}
      <Modal
        title={`主播统计数据 - ${selectedDateRange.start} 至 ${selectedDateRange.end}`}
        open={streamerModalVisible}
        onCancel={() => setStreamerModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStreamerModalVisible(false)}>关闭</Button>,
        ]}
        width={1600}
      >
        <Tabs
          defaultActiveKey="summary"
          items={[
            {
              key: 'summary',
              label: '汇总',
              children: (
                <div>
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(`主播统计汇总_${selectedDateRange.start}_${selectedDateRange.end}`, streamerColumns, streamerData)}
                    >
                      导出汇总
                    </Button>
                  </div>
                  <Table
                    rowKey="streamer_id"
                    dataSource={streamerData}
                    columns={streamerColumns}
                    pagination={false}
                    scroll={{ x: 1500 }}
                  />
                </div>
              ),
            },
            ...Object.keys(streamerDataByDate).map(dateStr => ({
              key: dateStr,
              label: dateStr,
              children: (
                <div>
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(`主播统计_${dateStr}`, streamerColumns, streamerDataByDate[dateStr])}
                    >
                      导出
                    </Button>
                  </div>
                  <Table
                    rowKey="streamer_id"
                    dataSource={streamerDataByDate[dateStr]}
                    columns={streamerColumns}
                    pagination={false}
                    scroll={{ x: 1500 }}
                  />
                </div>
              ),
            })),
          ]}
        />
      </Modal>

      {/* 游戏统计弹窗 */}
      <Modal
        title={`游戏统计数据 - ${selectedDateRange.start} 至 ${selectedDateRange.end}`}
        open={gameModalVisible}
        onCancel={() => setGameModalVisible(false)}
        footer={[
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(`游戏统计_${selectedDateRange.start}_${selectedDateRange.end}`, gameColumns, gameData)}
          >
            导出
          </Button>,
          <Button key="close" onClick={() => setGameModalVisible(false)}>关闭</Button>,
        ]}
        width={1400}
      >
        <Table
          rowKey="game_type_id"
          dataSource={gameData}
          columns={gameColumns}
          pagination={false}
          scroll={{ x: 1400 }}
        />
      </Modal>

      {/* 明细弹窗 */}
      <Modal
        title={`直播明细数据 - ${selectedDateRange.start} 至 ${selectedDateRange.end}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(`直播明细_${selectedDateRange.start}_${selectedDateRange.end}`, detailColumns, detailData)}
          >
            导出
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
        ]}
        width={1600}
      >
        <Table
          rowKey="id"
          dataSource={detailData}
          columns={detailColumns}
          pagination={false}
          scroll={{ x: 1600 }}
        />
      </Modal>

      {/* 用户列表弹窗 */}
      <Modal
        title={userListTitle || '用户列表'}
        open={userListModalVisible}
        onCancel={() => setUserListModalVisible(false)}
        footer={[
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(userListExportName || '用户列表', currentUserListColumns, userListData)}
          >
            导出
          </Button>,
          <Button key="close" onClick={() => setUserListModalVisible(false)}>关闭</Button>,
        ]}
        width={900}
      >
        <Table
          rowKey="user_id"
          dataSource={userListData}
          columns={currentUserListColumns}
          pagination={{ pageSize: 20 }}
          size="small"
          scroll={{ x: userListMode === 'bet' ? 800 : 700 }}
        />
      </Modal>
    </div>
  );
};

export default LiveStreamReport;

